<template>
  <SheetModal
    class="EditPlayerStatusModalView"
    data-theme="light"
    fit="hug"
    :aria-label="player ? `Edit ${player.name}’s status` : 'Edit the status'"
    :aria-busy="isRequesting"
    @close="$emit('close')"
  >
    <form
      class="EditPlayerStatusModalView__form"
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="EditPlayerStatusModalView__fieldset"
        :disabled="isRequesting"
      >
        <SheetHeader
          :title="player ? player.name : 'Loading…'"
          :isSubmitDisabled="!canSubmit"
          @cancel="$emit('close')"
        />

        <div class="EditPlayerStatusModalView__fieldGrid">
          <div class="EditPlayerStatusModalView__statusButtonListContainer">
            <RunStatusButtonList
              v-model="form.probability"
            />
          </div>
          <FormField
            label="Note"
            class="EditPlayerStatusModalView__noteField"
          >
            <FormInput
              v-model="form.note"
              name="note"
              autocomplete="off"
              placeholder="Arriving late, Leaving early, Make it rain, etc."
              enterkeyhint="done"
              :rows="3"
              isMultiline
              @keydown.enter.prevent="handleSubmit"
            />
          </FormField>
        </div>
      </fieldset>
    </form>
  </SheetModal>
</template>

<script lang="ts" setup>
import FormField from "@/components/FormField.vue";
import FormInput from "@/components/FormInput.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import useRunStore from "@/stores/useRunStore.ts";
import useStatusStore from "@/stores/useStatusStore.ts";
import { TablesUpdate } from "@shared/lib/database/database.types";
import { PlayerSummary, StatusSummaryWithPlayer } from "@shared/lib/database/query.types";
import { computed, onBeforeMount, ref } from "vue";
import RunStatusButtonList from "../RunStatusButtonList.vue";
import { EditPlayerStatusModalHistoryState } from "./EditPlayerStatusModalView.types.ts";

const emit = defineEmits<{
  close: [],
}>();

const {
  runId,
  playerId,
} = defineProps<{
  runId: string,
  playerId: string,
}>();

const {
  getStatus,
  fetchPlayerWithStatus,
  upsertStatus,
  deleteStatus,
} = useStatusStore();

const {
  fetchRun,
} = useRunStore();

const historyState = ref<EditPlayerStatusModalHistoryState | null>(history.state);
const isRequesting = ref(true);
const fetchedPlayer = ref<PlayerSummary | null>(null);
const fetchedStatus = ref<StatusSummaryWithPlayer | null | undefined>(undefined);

const existingStatus = computed(() => getStatus(runId, playerId));
const status = computed(() => {
  if (fetchedStatus.value !== undefined) return fetchedStatus.value;

  return existingStatus.value || historyState.value?.status;
});
const player = computed(() => existingStatus.value?.player || historyState.value?.player || fetchedPlayer.value);

const form = ref<{
  probability: number | undefined,
  note: string | null,
}>(getInitialForm());

onBeforeMount(async () => {
  const [error, playerWithStatus] = await fetchPlayerWithStatus(runId, playerId);

  if (error) {
    alert(error.message);
  }

  if (!playerWithStatus) {
    emit("close");
    return;
  }

  fetchedPlayer.value = playerWithStatus.player;
  fetchedStatus.value = playerWithStatus.status;
  form.value = getInitialForm();

  isRequesting.value = false;
});

const payload = computed((): TablesUpdate<"statuses"> => {
  return {
    probability: form.value.probability ?? undefined,
    note: form.value.note?.trim() || null,
  };
});

const isProbabilitySet = computed(() => {
  return form.value.probability !== null && form.value.probability !== undefined;
});

const canSubmit = computed(() => {
  return !!status.value || isProbabilitySet.value;
});

function getInitialForm (): {
  probability: number | undefined,
  note: string | null,
} {
  return {
    probability: status.value?.probability ?? undefined,
    note: status.value?.note || null,
  };
}

async function handleSubmit () {
  if (!canSubmit.value) return;

  isRequesting.value = true;

  if (isProbabilitySet.value) {
    const [error, status] = await upsertStatus(runId, playerId, payload.value);

    if (error) {
      alert(error.message);
    } else if (status) {
      fetchRun(runId).catch(() => {}); // TODO
      emit("close");
      return;
    }
  } else {
    const [error] = await deleteStatus(runId, playerId);

    if (error) {
      alert(error.message);
    } else {
      fetchRun(runId).catch(() => {}); // TODO
      emit("close");
      return;
    }
  }

  isRequesting.value = false;
}

</script>

<style lang="scss" scoped>
.EditPlayerStatusModalView__fieldset {
  display: grid;
  align-content: start;
  height: 100%;
}

.EditPlayerStatusModalView__fieldGrid {
  display: grid;
  padding: 0 0 calc(16px + var(--safeAreaInsetBottom));
}

.EditPlayerStatusModalView__statusButtonListContainer {
  padding: 20px 16px;
  border-bottom: solid 1px var(--themeColorNeutral100);
}

.EditPlayerStatusModalView__noteField {
  padding: 20px 16px 0;
}
</style>
